import { Delete } from '@mui/icons-material'
import {
  Autocomplete,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { memo, useCallback, useEffect, useRef, useState } from 'react'

import { createFilterOptions } from '@mui/material/Autocomplete'
import Counter from 'components/RouteWizard/Counter'

const SearchPassengers = ({ options, onChange, value, onAddFreeform }: any) => {
  const filter = createFilterOptions<any>()

  return (
    <Autocomplete
      sx={{ width: '100%' }}
      value={value ?? null}
      options={options}
      autoHighlight
      freeSolo
      size="small"
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params)
        const { inputValue } = params

        if (inputValue !== '') {
          filtered.push({
            inputValue,

            isFreeformAdd: true,
          })
        }
        return filtered
      }}
      isOptionEqualToValue={(option: any, val: any) => option?.id === val?.id}
      getOptionLabel={(option: any) => {
        if (!option) return ''
        if (typeof option === 'string') return option
        if (option.inputValue) return option.inputValue
        if (option.firstName || option.lastName) {
          return `${option.firstName ?? ''} ${option.lastName ?? ''} ${
            option.email ?? ''
          }`.trim()
        }
        if (option.name) return `${option.name} ${option.email ?? ''}`.trim()
        return option.email ?? ''
      }}
      onChange={(_event, selected) => {
        if (typeof selected === 'string') {
          onAddFreeform?.(selected)
          return
        }
        if (selected?.inputValue) {
          onAddFreeform?.(selected.inputValue)
          return
        }

        onChange(selected)
      }}
      renderOption={(props, option: any) => {
        if (option?.isFreeformAdd) {
          return (
            <Box component="li" {...props} key={`add-${option.inputValue}`}>
              Add “{option.inputValue}”
            </Box>
          )
        }
        return (
          <Box component="li" {...props}>
            <Box>
              <Typography>
                {option.firstName} {option.lastName}
              </Typography>
              <Typography sx={{ fontSize: 'var(--fs-12, 0.75rem)' }}>
                {option.email || option.contact.mobileNumber}
              </Typography>
            </Box>
          </Box>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search name..."
          inputProps={{
            ...params.inputProps,
          }}
        />
      )}
    />
  )
}

const LeadPassengerCard = ({
  passenger,
  editState,
  clientUserData,
  onLeadPassengerChange,
  saveLeadPassengerEdit,
}: {
  passenger: any
  editState: boolean
  onLeadPassengerChange: (field: string, value: string) => void
  clientUserData: any
  saveLeadPassengerEdit: any
}) => {
  const [detailChange, setDetailChange] = useState(false)

  return editState ? (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        alignItems: 'flex-start',
        justifyContent: 'space-around',
        minWidth: '30%',
      }}
    >
      <SearchPassengers
        options={clientUserData}
        value={null}
        onChange={(user: any) => {
          if (!user) return
          onLeadPassengerChange('name', `${user.firstName} ${user.lastName}`)
          onLeadPassengerChange('email', user.email ?? '')
          onLeadPassengerChange(
            'contactNumber',
            user.contact?.mobileNumber ?? '',
          )
          user.id && onLeadPassengerChange('userId', user.id)
          setDetailChange(true)
        }}
        onAddFreeform={(input: string) => {
          const name = input.trim()
          if (!name) return
          onLeadPassengerChange('name', name)
          onLeadPassengerChange('email', '')
          onLeadPassengerChange('contactNumber', '')
          onLeadPassengerChange('userId', undefined as any)
          setDetailChange(true)
        }}
      />
      <TextField
        label="Name"
        sx={{ width: '100%' }}
        value={passenger.name}
        onChange={(e) => {
          onLeadPassengerChange('name', e.target.value)
          setDetailChange(true)
        }}
        size="small"
      />
      <TextField
        label="Contact Number"
        sx={{ width: '100%' }}
        value={passenger.contactNumber}
        onChange={(e) => {
          onLeadPassengerChange('contactNumber', e.target.value)
          onLeadPassengerChange('userId', undefined)
          setDetailChange(true)
        }}
        size="small"
      />
      <TextField
        label="Email"
        sx={{ width: '100%' }}
        value={passenger.email}
        onChange={(e) => {
          onLeadPassengerChange('email', e.target.value)
          setDetailChange(true)
        }}
        size="small"
      />
      {detailChange && (
        <Button
          onClick={() => {
            saveLeadPassengerEdit()
            setDetailChange(false)
          }}
        >
          Save Lead Passenger
        </Button>
      )}
    </Box>
  ) : (
    <Box
      id="passengerCard"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left',
        gap: '8px',
      }}
    >
      <Box sx={{ display: 'flex', gap: '5px' }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {passenger?.name}
        </Typography>
      </Box>
      <Typography variant="body2">{passenger?.contactNumber}</Typography>
      <Typography variant="body2">{passenger?.email}</Typography>
    </Box>
  )
}
const PassengerForm = ({
  preparedQuote,
  clientUserData,
  editStates,
  updatePreparedQuote,
  updatePreparedAmend,
  numPassengers,
  setNumPassengers,
  maxPassengers,
}: any) => {
  console.log('ot', maxPassengers)

  const [personName, setPersonName] = useState<string[]>([])
  const leadPassenger = preparedQuote?.passengers.find(
    (p: any) => p.isLeadPassenger,
  )
  const [leadPassengerEdit, setLeadPassengerEdit] = useState({
    name: leadPassenger?.name ?? '',
    email: leadPassenger?.email ?? '',
    contactNumber: leadPassenger?.contactNumber ?? '',
  })

  useEffect(() => {
    setLeadPassengerEdit({
      name: leadPassenger?.name ?? '',
      email: leadPassenger?.email ?? '',
      contactNumber: leadPassenger?.contactNumber ?? '',
    })
  }, [leadPassenger])

  const handleLeadPassengerEditChange = (field: string, value: string) => {
    setLeadPassengerEdit((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  function updateLeadPassenger(
    passengers: any[],
    newDetails: {
      name?: string
      email?: string
      contactNumber?: string
      userId?: number
    },
  ): any[] {
    return passengers.map((passenger) =>
      passenger.isLeadPassenger
        ? {
            ...passenger,
            userId: newDetails.userId ?? undefined,
            name: newDetails.name ?? passenger.name,
            email: newDetails.email ?? passenger.email,
            contactNumber: newDetails.contactNumber ?? passenger.contactNumber,
          }
        : passenger,
    )
  }

  const saveLeadPassengerEdit = () => {
    const updatedPassengers = updateLeadPassenger(
      preparedQuote.passengers,
      leadPassengerEdit,
    )
    updatePreparedQuote('passengers', updatedPassengers)
    updatePreparedAmend('passengers', updatedPassengers)
  }

  const handleChange = useCallback(
    (event: SelectChangeEvent<typeof personName>) => {
      const {
        target: { value },
      } = event
      console.log('passengerChange', event)
      const selectedIds = Array.isArray(value) ? value : value.split(',')
      setPersonName(selectedIds)

      const removedIds = personName.filter((id) => !selectedIds.includes(id))

      if (removedIds.length > 0) {
        const updatedPassengers = preparedQuote?.passengers.filter(
          (passenger: any) => !removedIds.includes(passenger.userId.toString()),
        )
        updatePreparedQuote('passengers', updatedPassengers)
        updatePreparedAmend('passengers', updatedPassengers)
        return
      }

      const newPassenger = clientUserData.find(
        (user: any) =>
          selectedIds.includes(user.id.toString()) &&
          !preparedQuote?.passengers.some((p: any) => p.userId === user.id),
      )

      if (newPassenger) {
        updatePreparedQuote('passengers', [
          ...(preparedQuote?.passengers || []),
          {
            isLeadPassenger: false,
            userId: newPassenger.id,
            email: newPassenger.email,
            contactNumber: newPassenger.contact?.mobileNumber || '',
            name: `${newPassenger.firstName} ${newPassenger.lastName}`,
          },
        ])
        updatePreparedAmend('passengers', [
          ...(preparedQuote?.passengers || []),
          {
            isLeadPassenger: false,
            userId: newPassenger.id,
            email: newPassenger.email,
            contactNumber: newPassenger.contact?.mobileNumber || '',
            name: `${newPassenger.firstName} ${newPassenger.lastName}`,
          },
        ])
      }
    },
    [
      clientUserData,
      preparedQuote?.passengers,
      updatePreparedQuote,
      personName,
    ],
  )
  console.log(handleChange)
  const handleRemovePassenger = useCallback(
    (p: any) => {
      const updatedPassengers = preparedQuote?.passengers.filter(
        (passenger: any) => p !== passenger,
      )
      updatePreparedQuote('passengers', updatedPassengers)
      updatePreparedAmend('passengers', updatedPassengers)
      setPersonName((prev) => prev.filter((passenger) => p !== passenger))
    },
    [preparedQuote?.passengers, updatePreparedQuote],
  )

  const genId = () =>
    globalThis.crypto?.randomUUID
      ? globalThis.crypto.randomUUID()
      : `f-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

  const uniquePassengers = useCallback((arr: any[]) => {
    const map = new Map<string, any>()
    for (const p of arr) {
      const key = p.userId ? `u:${p.userId}` : `f:${p.localId}`
      if (!map.has(key)) map.set(key, p)
    }
    return Array.from(map.values())
  }, [])

  const [inlineEdits, setInlineEdits] = useState<
    Record<string, { contactNumber?: string; email?: string }>
  >({})

  const getRowId = useCallback((p: any, idx: number) => {
    return p.userId ? `user-${p.userId}` : `free-${p.name}-${idx}`
  }, [])

  const didNormalizeRef = useRef(false)
  useEffect(() => {
    if (didNormalizeRef.current) return
    const passengers = preparedQuote?.passengers || []
    if (!passengers.length) {
      didNormalizeRef.current = true
      return
    }

    const seen = new Set<string>()
    let changed = false
    const next = passengers.map((p: any) => {
      if (p.isLeadPassenger || p.userId) return p
      let id = p.localId as string | undefined
      if (!id || seen.has(id)) {
        let newId = genId()
        while (seen.has(newId)) newId = genId()
        seen.add(newId)
        changed = true
        return { ...p, localId: newId }
      }
      seen.add(id)
      return p
    })

    didNormalizeRef.current = true
    if (changed) {
      updatePreparedQuote('passengers', next)
    }
  }, [preparedQuote?.passengers, updatePreparedQuote])

  const didSyncRef = useRef(false)
  useEffect(() => {
    if (!didSyncRef.current) {
      didSyncRef.current = true
      return
    }
    updatePreparedAmend('passengers', preparedQuote?.passengers || [])
  }, [preparedQuote?.passengers, updatePreparedAmend])

  useEffect(() => {
    const ids = new Set(
      (preparedQuote?.passengers || [])
        .filter((p: any) => !p.isLeadPassenger)
        .map((p: any, idx: number) => getRowId(p, idx)),
    )
    setInlineEdits((prev) =>
      Object.fromEntries(Object.entries(prev).filter(([k]) => ids.has(k))),
    )
  }, [preparedQuote?.passengers, getRowId])

  const commitInlineEdit = useCallback(
    (rowId: string) => {
      const patch = inlineEdits[rowId]
      if (!patch) return
      const next = preparedQuote?.passengers.map((p: any, idx: number) =>
        getRowId(p, idx) === rowId ? { ...p, ...patch } : p,
      )
      updatePreparedQuote('passengers', next)
      updatePreparedAmend('passengers', next)
      setInlineEdits((prev) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [rowId]: _omit, ...rest } = prev
        console.log(_omit)
        return rest
      })
    },
    [
      inlineEdits,
      preparedQuote?.passengers,
      updatePreparedQuote,
      updatePreparedAmend,
      getRowId,
    ],
  )

  function addAdditionalPassengerFromUser(
    user: any,
    preparedQuote: any,
    updatePreparedQuote: any,
    updatePreparedAmend: any,
  ) {
    if (!user) return
    const newP = {
      isLeadPassenger: false,
      userId: user.id,
      email: user.email ?? '',
      contactNumber: user.contact?.mobileNumber || '',
      name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
    }
    const existing = preparedQuote?.passengers || []

    const next = uniquePassengers([...existing, newP])
    updatePreparedQuote('passengers', next)

    updatePreparedAmend('passengers', next)
  }

  function addAdditionalPassengerFromFreeform(
    input: string,
    preparedQuote: any,
    updatePreparedQuote: any,
    updatePreparedAmend: any,
  ) {
    const name = input.trim()
    if (!name) return

    const existing = preparedQuote?.passengers || []

    const used = new Set(
      existing.filter((p: any) => !p.userId).map((p: any) => p.localId),
    )
    let id = genId()
    while (used.has(id)) id = genId()

    const newP = {
      isLeadPassenger: false,
      userId: undefined,
      localId: id,
      email: '',
      contactNumber: '',
      name,
    }

    const next = uniquePassengers([...existing, newP])
    updatePreparedQuote('passengers', next)

    updatePreparedAmend('passengers', next)
  }
  console.log('numPassengers', numPassengers)
  return (
    <Grid container sx={{ display: 'flex', gap: '10px', flexDirection: 'row' }}>
      {leadPassenger && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            justifyContent: 'space-around',
            width: '40%',
          }}
        >
          <Typography variant="h6">Lead Passenger</Typography>
          <LeadPassengerCard
            passenger={{ ...leadPassenger, ...leadPassengerEdit }}
            clientUserData={clientUserData}
            editState={editStates.passengers}
            onLeadPassengerChange={handleLeadPassengerEditChange}
            saveLeadPassengerEdit={saveLeadPassengerEdit}
          />
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: '15px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h6">Additional Passengers</Typography>
          <Counter
            value={numPassengers}
            label="additional passenger"
            negativeOnClick={(num) => {
              setNumPassengers(num)
              const newTotal = num + 1
              updatePreparedQuote('numberOfPassengers', newTotal)
              updatePreparedAmend('numberOfPassengers', newTotal)

              const currentPassengers = preparedQuote?.passengers || []
              const nonLeadPassengers = currentPassengers.filter(
                (p) => !p.isLeadPassenger,
              )

              if (nonLeadPassengers.length > num) {
                const keepPassengers = [
                  ...currentPassengers.filter((p) => p.isLeadPassenger),
                  ...nonLeadPassengers.slice(0, num),
                ]
                updatePreparedQuote('passengers', keepPassengers)
                updatePreparedAmend('passengers', keepPassengers)
              }
            }}
            negativeVisible={editStates.passengers}
            negativeDisabled={numPassengers === 0}
            positiveVisible={editStates.passengers}
            positiveDisabled={numPassengers >= maxPassengers - 1}
            positiveOnClick={(num) => {
              if (maxPassengers === undefined || num <= maxPassengers - 1) {
                setNumPassengers(num)
                const newTotal = num + 1 // +1 for lead passenger
                updatePreparedQuote('numberOfPassengers', newTotal)
                updatePreparedAmend('numberOfPassengers', newTotal)
              }
              window.scrollTo(0, document.body.scrollHeight)
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', gap: '10px', position: 'relative' }}>
          {numPassengers > 0 && (
            <TableContainer component={Paper}>
              <Table
                sx={{ minWidth: '300px', fontSize: 'var(--fs-12, 0.75rem)' }}
                size="small"
                aria-label="table of additional passengers"
              >
                {preparedQuote?.passengers.length > 1 ? (
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ p: 1, m: 0 }}>Name</TableCell>
                      <TableCell sx={{ p: 1, m: 0 }}>Contact Number</TableCell>
                      <TableCell sx={{ p: 1, m: 0 }}>Email Address</TableCell>
                      <TableCell sx={{ p: 1, m: 0 }}></TableCell>
                    </TableRow>
                  </TableHead>
                ) : (
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={4}>
                        No additional passenger names provided
                      </TableCell>
                    </TableRow>
                  </TableHead>
                )}
                <TableBody>
                  {preparedQuote?.passengers.map(
                    (passenger: any, idx: number) => {
                      const names = passenger.name.split(' ')
                      const rowId = getRowId(passenger, idx)
                      const draft = inlineEdits[rowId] || {}

                      return (
                        !passenger.isLeadPassenger && (
                          <TableRow key={rowId}>
                            <TableCell sx={{ p: 1, m: 0 }}>
                              {`${names[0]} ${names[1]?.[0] || ''}.`}
                            </TableCell>
                            <TableCell sx={{ p: 0, m: 0 }}>
                              {!passenger.userId && editStates.passengers ? (
                                <TextField
                                  size="small"
                                  placeholder="Contact Number"
                                  value={
                                    draft.contactNumber ??
                                    passenger.contactNumber ??
                                    ''
                                  }
                                  onChange={(e) =>
                                    setInlineEdits((prev) => ({
                                      ...prev,
                                      [rowId]: {
                                        ...prev[rowId],
                                        contactNumber: e.target.value,
                                      },
                                    }))
                                  }
                                  onBlur={() => commitInlineEdit(rowId)}
                                  inputProps={{ inputMode: 'tel' }}
                                />
                              ) : (
                                `${passenger.contactNumber ?? ''}`
                              )}
                            </TableCell>
                            <TableCell sx={{ p: 0, m: 0 }}>
                              {!passenger.userId && editStates.passengers ? (
                                <TextField
                                  size="small"
                                  placeholder="Email address"
                                  value={draft.email ?? passenger.email ?? ''}
                                  onChange={(e) =>
                                    setInlineEdits((prev) => ({
                                      ...prev,
                                      [rowId]: {
                                        ...prev[rowId],
                                        email: e.target.value,
                                      },
                                    }))
                                  }
                                  onBlur={() => commitInlineEdit(rowId)}
                                  inputProps={{ inputMode: 'email' }}
                                />
                              ) : (
                                `${passenger.email ?? ''}`
                              )}
                            </TableCell>
                            {editStates.passengers && (
                              <TableCell sx={{ p: 0, m: 0 }}>
                                <IconButton
                                  onClick={() =>
                                    handleRemovePassenger(passenger)
                                  }
                                >
                                  <Delete />
                                </IconButton>
                              </TableCell>
                            )}
                          </TableRow>
                        )
                      )
                    },
                  )}
                </TableBody>
              </Table>
              {editStates.passengers &&
                numPassengers > preparedQuote.passengers.length - 1 && (
                  <SearchPassengers
                    options={clientUserData}
                    value={null}
                    onChange={(user: any) =>
                      addAdditionalPassengerFromUser(
                        user,
                        preparedQuote,
                        updatePreparedQuote,
                        updatePreparedAmend,
                      )
                    }
                    onAddFreeform={(input: string) =>
                      addAdditionalPassengerFromFreeform(
                        input,
                        preparedQuote,
                        updatePreparedQuote,
                        updatePreparedAmend,
                      )
                    }
                  />
                )}
            </TableContainer>
          )}
        </Box>
      </Box>
    </Grid>
  )
}

export default memo(PassengerForm)
